import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Shield, Smartphone, Copy, Check, Loader2, Link, Unlink, Eye, EyeOff, TrendingUp } from "lucide-react";
import { API_URL } from "@/utils/apiConfig";
import { ChangePasswordCard } from "@/components/settings/ChangePasswordCard";

const API = API_URL;

export function SecurityTab({ authAxios, restaurant }) {
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [twoFaStatus, setTwoFaStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [investorToken, setInvestorToken] = useState(restaurant?.investor_token || "");
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    authAxiosRef.current.get("/2fa/status")
      .then(r => setTwoFaStatus(r.data))
      .catch(() => {});
  }, []);

  const handleSetup2FA = async () => {
    setLoading(true);
    try {
      const res = await authAxios.post("/2fa/setup");
      setSetupData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    }
    setLoading(false);
  };

  const handleVerify2FA = async () => {
    if (!verifyCode || verifyCode.length < 6) { toast.error("Codigo deve ter 6 digitos"); return; }
    setLoading(true);
    try {
      const res = await authAxios.post("/2fa/verify", { code: verifyCode });
      setBackupCodes(res.data.backup_codes);
      setTwoFaStatus({ enabled: true, has_secret: true });
      setSetupData(null);
      setVerifyCode("");
      toast.success("2FA ativado!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Codigo invalido");
    }
    setLoading(false);
  };

  const handleDisable2FA = async () => {
    if (!disableCode) { toast.error("Informe o codigo"); return; }
    setLoading(true);
    try {
      await authAxios.post("/2fa/disable", { code: disableCode });
      setTwoFaStatus({ enabled: false, has_secret: false });
      setDisableCode("");
      setBackupCodes(null);
      toast.success("2FA desativado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Codigo invalido");
    }
    setLoading(false);
  };

  const handleGenerateInvestorToken = async () => {
    try {
      const res = await authAxios.post("/investor/generate-token");
      setInvestorToken(res.data.token);
      toast.success("Link do investidor gerado!");
    } catch { toast.error("Erro"); }
  };

  const handleRevokeInvestorToken = async () => {
    try {
      await authAxios.delete("/investor/revoke-token");
      setInvestorToken("");
      toast.success("Token revogado");
    } catch { toast.error("Erro"); }
  };

  const investorUrl = investorToken ? `${API.replace("/api", "")}/investor/${restaurant?.id}?token=${investorToken}` : "";

  const [copied, setCopied] = useState(false);
  const copyInvestorLink = () => {
    navigator.clipboard.writeText(investorUrl);
    setCopied(true);
    toast.success("Link copiado!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      {/* 2FA */}
      <Card data-testid="2fa-settings-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Shield className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-base">Autenticacao em Dois Fatores (2FA)</CardTitle>
              <CardDescription>Proteja sua conta com Google Authenticator</CardDescription>
            </div>
            <Badge className={twoFaStatus?.enabled ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}>
              {twoFaStatus?.enabled ? "Ativo" : "Inativo"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!twoFaStatus?.enabled && !setupData && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                Adicione uma camada extra de seguranca a sua conta. Ao ativar o 2FA, voce precisara de um codigo do Google Authenticator ou Authy para fazer login.
              </p>
              <Button onClick={handleSetup2FA} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="enable-2fa-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
                Ativar 2FA
              </Button>
            </div>
          )}

          {setupData && (
            <div className="space-y-4" data-testid="2fa-setup-section">
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <p className="text-sm font-medium text-gray-700 mb-3">Escaneie o QR Code com o Google Authenticator:</p>
                <img src={setupData.qr_code} alt="QR Code 2FA" className="mx-auto w-44 h-44 rounded-lg border" data-testid="2fa-qr-code" />
                <div className="mt-3">
                  <p className="text-[10px] text-gray-400 mb-1">Ou insira o codigo manualmente:</p>
                  <code className="text-xs font-mono bg-white px-3 py-1.5 rounded border select-all" data-testid="2fa-secret-code">{setupData.secret}</code>
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1.5 block">Digite o codigo de 6 digitos do app:</Label>
                <div className="flex gap-2">
                  <Input
                    value={verifyCode}
                    onChange={e => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-xl font-mono tracking-[0.3em] h-12"
                    maxLength={6}
                    data-testid="2fa-verify-input"
                  />
                  <Button onClick={handleVerify2FA} disabled={loading || verifyCode.length < 6} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5 h-12 px-6" data-testid="2fa-verify-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Verificar
                  </Button>
                </div>
              </div>
            </div>
          )}

          {backupCodes && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="2fa-backup-codes">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-bold text-amber-800">Codigos de Backup</h4>
                <Button size="sm" variant="ghost" onClick={() => setShowBackupCodes(!showBackupCodes)} className="text-amber-600 gap-1 text-xs">
                  {showBackupCodes ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {showBackupCodes ? "Ocultar" : "Mostrar"}
                </Button>
              </div>
              <p className="text-xs text-amber-600 mb-3">Guarde estes codigos em local seguro. Cada codigo so pode ser usado uma vez.</p>
              {showBackupCodes && (
                <div className="grid grid-cols-4 gap-1.5">
                  {backupCodes.map((code, i) => (
                    <code key={i} className="text-xs font-mono bg-white px-2 py-1.5 rounded text-center border">{code}</code>
                  ))}
                </div>
              )}
            </div>
          )}

          {twoFaStatus?.enabled && !setupData && (
            <div className="space-y-3">
              <p className="text-sm text-emerald-600 font-medium">2FA esta ativo na sua conta</p>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-xs text-gray-500 mb-1.5 block">Para desativar, insira o codigo 2FA:</Label>
                  <Input
                    value={disableCode}
                    onChange={e => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
                    placeholder="Codigo 2FA ou backup"
                    className="h-10 font-mono"
                    maxLength={8}
                    data-testid="2fa-disable-input"
                  />
                </div>
                <Button variant="destructive" onClick={handleDisable2FA} disabled={loading || !disableCode} className="gap-1.5 h-10" data-testid="2fa-disable-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Desativar"}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Password */}
      <ChangePasswordCard authAxios={authAxios} />

      {/* Investor Mode */}
      <Card data-testid="investor-settings-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-base">Modo Investidor</CardTitle>
              <CardDescription>Compartilhe metricas financeiras com investidores via link seguro</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {investorToken ? (
            <>
              <div>
                <Label className="text-xs text-gray-500 mb-1.5 block">Link do Dashboard do Investidor:</Label>
                <div className="flex gap-2">
                  <Input value={investorUrl} readOnly className="font-mono text-xs bg-gray-50" data-testid="investor-link-input" />
                  <Button size="sm" variant="outline" onClick={copyInvestorLink} className="gap-1.5 flex-shrink-0" data-testid="copy-investor-link">
                    {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? "Copiado" : "Copiar"}
                  </Button>
                </div>
              </div>
              <Button variant="destructive" size="sm" onClick={handleRevokeInvestorToken} className="gap-1.5" data-testid="revoke-investor-btn">
                <Unlink className="w-3.5 h-3.5" /> Revogar Acesso
              </Button>
            </>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                Gere um link seguro para que investidores possam visualizar o desempenho financeiro do seu restaurante sem precisar de login.
              </p>
              <Button onClick={handleGenerateInvestorToken} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="generate-investor-btn">
                <Link className="w-4 h-4" /> Gerar Link do Investidor
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
